import { AxiosResponse } from 'axios'
import { api } from '@/lib/api-client.ts'
import { components } from '@/lib/types/api-types.ts'
import { QueryKey } from '@tanstack/react-query'
import { User } from '@/store/user'
import type { AnnotationDocument } from '@/lib/annotation/schema'
import { safeRandomUUID } from '@/lib/helpers/safeRandomUUID.ts'
export const userQueryKey: QueryKey = ['user', 'me']

type FileUpload = components['schemas']['MediaObject.jsonld-media_object.read']
export const uploadFile = async ({
  file,
  owningProjectStage,
  owningIssue,
  owningComment,
  uuid,
  fileUUID,
  annotationData,
}: {
  file: File
  owningProjectStage?: string | undefined | null
  /**
   * Optional Issue IRI (e.g. "/issues/5"). When set, the MediaObject is
   * created pre-linked to the issue's attachments collection — no
   * follow-up PATCH /issues/{id} needed. Use this when uploading to an
   * existing issue (AttachmentsManager). Voter check becomes ISSUE_EDIT.
   */
  owningIssue?: string | undefined | null
  /**
   * Optional Comment IRI. Same pattern as owningIssue but for comment
   * attachments. Voter check becomes COMMENT_EDIT.
   */
  owningComment?: string | undefined | null
  uuid?: string
  fileUUID?: string
  /**
   * Optional vector annotation document. When present, the camera-captured
   * photo is uploaded WITH the markup as a single multipart POST — backend
   * decodes the JSON string in setAnnotationData. Picker uploads pass undefined.
   */
  annotationData?: AnnotationDocument | null
}): Promise<AxiosResponse<FileUpload>> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('fileUUID', fileUUID ?? safeRandomUUID())
  if (owningProjectStage) {
    formData.append('owningProjectStage', owningProjectStage)
  }
  if (owningIssue) {
    formData.append('owningIssue', owningIssue)
  }
  if (owningComment) {
    formData.append('owningComment', owningComment)
  }
  if (uuid) {
    formData.append('requestUUID', uuid)
  }
  if (annotationData) {
    const serialized = JSON.stringify(annotationData)
    formData.append('annotationData', serialized)
    console.info(
      '[anno-trace] uploadFile attached annotationData ' +
        JSON.stringify({
          fileUUID,
          itemsCount: annotationData.items?.length ?? 0,
          bytes: serialized.length,
          first50: serialized.slice(0, 50),
        }),
    )
  } else {
    console.info(
      '[anno-trace] uploadFile called WITHOUT annotationData ' +
        JSON.stringify({
          fileUUID,
          annotationDataValue: annotationData,
        }),
    )
  }

  return api.post(`/media_objects`, formData)
}

/**
 * PATCH the annotation overlay on an existing MediaObject. Returns mock 202
 * on every platform (Dexie queue intercepts). Caller should optimistic-patch
 * the lightbox UI; canonical state arrives via Mercure when the queue syncs.
 *
 * `annotationRequestUUID` is generated per save and echoed in the postUpdate
 * Mercure payload — see api/src/EventListener/MediaObjectListener.php.
 */
export const patchMediaObjectAnnotation = async ({
  id,
  annotationData,
  annotationRequestUUID,
}: {
  id: number | string
  annotationData: import('@/lib/annotation/schema').AnnotationDocument | null
  annotationRequestUUID: string
}): Promise<AxiosResponse<FileUpload>> => {
  return api.patch(
    `/media_objects/${id}`,
    { annotationData, annotationRequestUUID },
    { headers: { 'Content-Type': 'application/merge-patch+json' } },
  )
}

/**
 * Commit a pending lightbox rotate. Mutates `displayLarge` + thumbnails
 * server-side; `canonicalEvidence` stays byte-identical (PR 3 invariant).
 *
 * **Bypasses the native Dexie queue** via the `QUEUE_BYPASS_PATTERNS`
 * rule in `@/lib/queueBypass`. Offline callers must catch the network
 * error and surface a toast — there is no draft-shape to queue.
 *
 * `rotationRequestUUID` is required: the server echoes it on the
 * Mercure topic `/bg_sync/media_objects/rotate/{rotationRequestUUID}`
 * so `useMediaObjectRotationSubscriber` can clear the matching
 * optimistic store entry.
 */
export const rotateMediaObject = async ({
  id,
  degrees,
  rotationRequestUUID,
}: {
  id: number | string
  degrees: 90 | 180 | 270
  rotationRequestUUID: string
}): Promise<AxiosResponse<FileUpload>> => {
  return api.post(`/media_objects/${id}/rotate`, {
    degrees,
    rotationRequestUUID,
  })
}

export const currentUserQuery = () => ({
  queryKey: userQueryKey,
  queryFn: () => getCurrentUser(),
})

export const getCurrentUser = async function () {
  const res: AxiosResponse = await api.get('/users/me')
  const userData: User = res.data
  return userData
}
